import { getVariableValue } from "@/shared/utils/resolve-tokens";
import { isDateVariableName, resolveDateToken } from "./date-resolver";
import { parseDateToken } from "./date-token";

// Double-brace tokens (pills), excluding triple-brace `{{{blocks}}}`.
const TOKEN_RE = /\{\{(?!\{)\s*([^{}]+?)\s*\}\}(?!\})/g;

/**
 * Replaces date-variable tokens (`{{current_date+5d|FORMAT}}`) in a content
 * string with their computed, formatted value. Must run BEFORE Handlebars: the
 * `+`/`|`/`/` characters in a date token would otherwise be mangled by the
 * Handlebars sanitizer (it only recognizes `[a-zA-Z0-9_.-]` tokens). Non-date
 * tokens are left untouched for Handlebars to resolve.
 *
 * `now` fixes the base date for `current_date`/`today`; other date variables
 * use their sender-provided value from `data`.
 *
 * `extraDateNames` carries the org's user-created `date` variables (see
 * `useCustomDateNames`). Without them an offset on a CUSTOM date variable
 * (`{{contract_start+1y}}`) is not recognized as a date token, so it survives
 * to Handlebars — whose sanitizer only accepts `[a-zA-Z0-9_.-]` names and
 * therefore blanks the whole token. Callers rendering real content MUST pass
 * this; the parameter is optional only so the built-in-only call sites and
 * tests stay unchanged.
 */
export const bakeDateTokens = (
  content: string,
  data: Record<string, unknown>,
  now: Date,
  extraDateNames?: ReadonlySet<string>,
): string => {
  if (!content || content.indexOf("{{") === -1) return content;
  return content.replace(TOKEN_RE, (match, rawName: string) => {
    const token = rawName.trim();
    const { name } = parseDateToken(token);
    if (!isDateVariableName(name, extraDateNames)) return match;
    return resolveDateToken(token, {
      now,
      baseValue: getVariableValue(data, name),
    });
  });
};
